import { productSchema as algoliaProductSchema } from '@integration-layer/.nuxt/armani/product-schema'
import { z } from 'zod'

/**
 * Composable for managing Algolia product-related operations.
 * Provides methods to load and index products from Algolia.
 *
 * @returns {Object} An object containing methods for product retrieval
 * @property {Function} loadAlgoliaProducts - Loads products by their product codes
 */
export const useAlgoliaProducts = () => {
  // Get the current Algolia index from app configuration
  const algoliaIndex = useAppConfig().currentAlgoliaIndex

  /**
   * Loads Algolia products by their product codes.
   *
   * @param {string[]} productCodes - Array of product codes to retrieve
   * @returns {Promise<Record<string, z.infer<typeof algoliaProductSchema>>>}
   * A promise resolving to an object of products indexed by their objectID
   *
   * @example
   * const products = await loadAlgoliaProducts(['ABC123', 'DEF456']);
   */
  const loadAlgoliaProducts = async (productCodes: string[]) => {
    const algoliaData = await $fetch(`/api/getProducts/${algoliaIndex}`, {
      query: {
        mfcList: productCodes.join(','),
      },
    })
    const algoliaProducts = algoliaData.filter(isNonNullable)
    return indexByObjectId(algoliaProducts)
  }

  /**
   * Indexes an array of products by their objectID.
   *
   * @param {z.infer<typeof algoliaProductSchema>[]} products - Array of products to index
   * @returns {Record<string, z.infer<typeof algoliaProductSchema>>}
   * An object with products indexed by their objectID
   *
   * @private
   * @example
   * const indexedProducts = indexByObjectId(products);
   * // Returns { 'objectID1': product1, 'objectID2': product2 }
   */
  const indexByObjectId = (products: z.infer<typeof algoliaProductSchema>[]) =>
    products.reduce(
      (productsByObjectId, product) => ({
        ...productsByObjectId,
        [product.objectID]: product,
      }),
      {} as Record<string, z.infer<typeof algoliaProductSchema>>
    )

  return {
    loadAlgoliaProducts,
  }
}
